import { createSlice } from '@reduxjs/toolkit'
import { tenantSubdomainFromHost } from '@/utils/hostTenant'

/**
 * Workspace subdomain routing: the workspace the page's host names
 * (riverbend.graphit.io -> "riverbend") and whether it has been applied to
 * this session yet. The switch itself lives in useHostTenant.js.
 *
 * Applied once per sign-in, not continuously: after it, an in-place switch
 * (a new organization, an accepted invitation) stands.
 *
 * Deliberately free of authSlice imports - it reacts to auth actions by type
 * string, like tenantSwitchActionType.js explains - so the store can import it
 * without re-entering the authSlice -> api/client -> store cycle.
 */

export const HOST_TENANT_STATUS = Object.freeze({
  /** Not a workspace host (app.graphit.io, localhost, ...). */
  NONE: 'none',
  /** A workspace host, waiting for a verified session to apply it to. */
  PENDING: 'pending',
  APPLYING: 'applying',
  APPLIED: 'applied',
  /** The signed-in user has no membership in the host's workspace. */
  NOT_MEMBER: 'not_member',
})

const UNSETTLED = new Set([HOST_TENANT_STATUS.PENDING, HOST_TENANT_STATUS.APPLYING])

/**
 * Auth actions after which the next sign-in must apply the host tenant again:
 * the `logout` reducer and the `logoutUser` thunk (both "auth/logout"), and
 * /auth/me refusing the session.
 */
const SESSION_ENDED_TYPES = new Set([
  'auth/logout',
  'auth/logout/fulfilled',
  'auth/logout/rejected',
  'auth/fetchCurrentUser/rejected',
])

/**
 * @param {string|null} [subdomain] - defaults to the page host's workspace
 * @returns {{subdomain: string|null, status: string}}
 */
export function hostTenantInitialState(subdomain = tenantSubdomainFromHost()) {
  return {
    subdomain: subdomain || null,
    status: subdomain ? HOST_TENANT_STATUS.PENDING : HOST_TENANT_STATUS.NONE,
  }
}

const hostTenantSlice = createSlice({
  name: 'hostTenant',
  initialState: hostTenantInitialState,
  reducers: {
    hostTenantApplying: (state) => {
      state.status = HOST_TENANT_STATUS.APPLYING
    },
    /** @param {{payload: 'applied'|'not_member'}} action */
    hostTenantResolved: (state, action) => {
      state.status = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => SESSION_ENDED_TYPES.has(action.type),
      (state) => {
        if (state.subdomain) state.status = HOST_TENANT_STATUS.PENDING
      },
    )
  },
})

export const { hostTenantApplying, hostTenantResolved } = hostTenantSlice.actions

/** @returns {{subdomain: string|null, status: string}} */
export const selectHostTenant = (state) => state.hostTenant ?? hostTenantInitialState(null)

/**
 * A signed-in user on a workspace host whose workspace is not settled yet:
 * tenant-scoped pages wait, and nothing routes to the tenant picker.
 * @returns {boolean}
 */
export const selectIsHostTenantPending = (state) =>
  Boolean(state.auth?.isAuthenticated) && UNSETTLED.has(state.hostTenant?.status)

/**
 * The host's subdomain when the signed-in user is not a member of it.
 * @returns {string|null}
 */
export const selectHostTenantNotMemberSubdomain = (state) =>
  state.hostTenant?.status === HOST_TENANT_STATUS.NOT_MEMBER ? state.hostTenant.subdomain : null

export default hostTenantSlice.reducer
